import { useState } from 'react';
import { Link } from 'react-router-dom';
import AuthPageShell from './components/AuthPageShell';
import { requestPasswordReset } from './utils/apiAuth';
import { passwordErrorMessage } from './utils/passwordRules';

// The API answers identically whether or not the address has an account, so
// the form can never be used to find out which addresses are registered. Do
// not add a "no such account" branch here.
const SENT_MESSAGE =
  'If that address has an account, a reset link is on its way. The link is valid for 12 hours.';

function ForgottenPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await requestPasswordReset(email.trim());
      setSent(true);
    } catch (err) {
      setError(passwordErrorMessage(err, 'Could not send the reset link. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <AuthPageShell title="Check your inbox">
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <p className="text-sm text-green-700 dark:text-green-300">{SENT_MESSAGE}</p>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Nothing arrived? Check the spam folder, then{' '}
          <button
            type="button"
            onClick={() => setSent(false)}
            className="link underline hover:no-underline"
          >
            send another link
          </button>
          . Requesting a new one immediately invalidates the previous email.
        </p>
        <Link
          to="/login"
          className="mt-6 block w-full h-12 rounded-lg bg-primary px-6 text-base font-bold text-white hover:bg-primary/90 transition-colors text-center leading-[3rem]"
        >
          Back to login
        </Link>
      </AuthPageShell>
    );
  }

  return (
    <AuthPageShell title="Forgotten password">
      <p className="mb-6 text-sm text-gray-600 dark:text-gray-400">
        Enter the email address on your account and we will send you a link to set a new password.
        Your current password keeps working until you do.
      </p>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            Email
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={loading}
            placeholder="your@email.com"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-background-dark text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full flex h-12 items-center justify-center rounded-lg bg-primary px-6 text-base font-bold text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? 'Sending...' : 'Send reset link'}
        </button>
      </form>

      <p className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
        Remembered it?{' '}
        <Link to="/login" className="link">
          Back to login
        </Link>
      </p>
    </AuthPageShell>
  );
}

export default ForgottenPassword;
